import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';

interface RecentPortvilleStayRequest {
  id: string;
  type: 'extension' | 'moveOut';
  roomId?: string;
  roomNumber: string;
  residentInfo: {
    name: string;
    organization: string;
    phoneNumber: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'applied' | 'contracted' | 'closed';
  appliedAt: Date;
  extensionRound?: 1 | 2;
  moveOutReason?: string;
  desiredMoveOutDate?: Date;
}

interface RecentMoveOutRequestsProps {
  requests: RecentPortvilleStayRequest[];
}

const RecentMoveOutRequests: React.FC<RecentMoveOutRequestsProps> = ({ requests }) => {
  const navigate = useNavigate();
  const { hasPermission, userLevel } = useAdminAuth();

  const canViewPortvilleRooms = userLevel === 1 || hasPermission('portvilleRooms');

  const handleRequestClick = (request: RecentPortvilleStayRequest) => {
    // 최고관리자이거나 portvilleRooms 권한이 있는 경우
    if (!canViewPortvilleRooms) return;

    if (request.type === 'extension' && request.roomId && request.extensionRound) {
      navigate(`/admin/portville-rooms/extension-request/${request.roomId}?round=${request.extensionRound}`);
    } else {
      navigate(`/admin/portville-rooms`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
      case 'applied':
        return 'text-yellow-600 bg-yellow-100';
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      case 'closed':
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: RecentPortvilleStayRequest['status']) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'applied':
        return '신청됨';
      case 'approved':
        return '승인됨';
      case 'rejected':
        return '거절됨';
      case 'closed':
        return '퇴거완료로 종결';
      case 'completed':
        return '완료됨';
      case 'contracted':
        return '계약완료';
      default:
        return status;
    }
  };

  const getMoveOutReasonText = (reason: string) => {
    switch (reason) {
      case 'personnel_change':
        return '인사변경';
      case 'personal_reason':
        return '개인사유';
      default:
        return reason;
    }
  };

  const getRequestTypeLabel = (request: RecentPortvilleStayRequest) => {
    if (request.type === 'extension') {
      return request.extensionRound === 2 ? '2차 연장' : '연장';
    }

    return '퇴거';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 연장 / 퇴거 신청</h2>
      <div className="space-y-4">
        {requests.length === 0 ? (
          <p className="text-gray-500 text-sm">최근 연장 / 퇴거 신청 내역이 없습니다.</p>
        ) : (
          requests.map((request) => (
            <div
              key={request.id}
              className={`border-b border-gray-200 pb-4 last:border-0 p-2 rounded-lg transition-colors ${
                canViewPortvilleRooms ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={() => handleRequestClick(request)}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 pr-1">
                  <p className="font-medium text-gray-900 truncate">
                    {request.roomNumber}호 - {request.residentInfo.name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{request.residentInfo.organization}</p>
                  {request.type === 'moveOut' && request.moveOutReason && (
                    <>
                      <p className="text-sm text-gray-500 truncate">
                        {getMoveOutReasonText(request.moveOutReason)}
                      </p>
                      <p className="text-sm text-gray-500">
                        희망일: {request.desiredMoveOutDate ? format(request.desiredMoveOutDate, 'yyyy년 M월 d일') : '-'}
                      </p>
                    </>
                  )}
                  <p className="text-sm text-gray-500">
                    신청일: {request.appliedAt ? format(request.appliedAt, 'yyyy년 M월 d일') : '-'}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="px-2 py-1 text-xs font-medium rounded-full text-indigo-600 bg-indigo-100">
                    {getRequestTypeLabel(request)}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      request.status
                    )}`}
                  >
                    {getStatusText(request.status)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentMoveOutRequests; 
